/**
 * @license Codistica
 *
 * Copyright (c) 2020, Codistica and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.md file in the root directory of this source tree.
 *
 * BUILD DATE: Fri Jul 10 2020 16:51:08 GMT+0200 (Central European Summer Time)
 */
import _newArrowCheck from "@babel/runtime-corejs3/helpers/esm/newArrowCheck";
import _bindInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/bind";
import _classCallCheck from "@babel/runtime-corejs3/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime-corejs3/helpers/esm/createClass";
import _assertThisInitialized from "@babel/runtime-corejs3/helpers/esm/assertThisInitialized";
import _inherits from "@babel/runtime-corejs3/helpers/esm/inherits";
import _createSuper from "@babel/runtime-corejs3/helpers/esm/createSuper";
import _defineProperty from "@babel/runtime-corejs3/helpers/esm/defineProperty";

/** @module react/components/dot-navigation */
import React from 'react';
import resetClassNames from '../../css/reset.module.scss';
import { mergeClassNames } from '../../modules/merge-class-names.js';
import { mergeStyles } from '../../modules/merge-styles.js';
import componentClassNames from './index.module.scss';

/**
 * @typedef dotNavigationPropsType
 * @property {number} [quantity=0] - Number of dots.
 * @property {number} [dotIndex=0] - Active dot index.
 * @property {string} [size='15px'] - Dot diameter in pixels.
 * @property {string} [direction='row'] - Dots direction.
 * @property {boolean} [auto=true] - Automatically change active dot index on switch.
 * @property {Function} [onSwitch=null] - Callback for switch event.
 * @property {boolean} [waitForCallback=false] - Disable dot navigation until onSwitch callback is executed.
 * @property {Object<string,*>} [style={}] - React prop.
 * @property {string} [className=''] - React prop.
 * @property {Object<string,*>} [customStyles={}] - Custom styles prop.
 * @property {Object<string,*>} [customClassNames={}] - Custom classNames prop.
 * @property {('default'|string|null)} [globalTheme='default'] - Global theme to be used.
 */

/**
 * @classdesc A generic dot navigation component.
 */
var DotNavigation = /*#__PURE__*/function (_React$Component) {
  _inherits(DotNavigation, _React$Component);

  var _super = _createSuper(DotNavigation);

  /**
   * @description Constructor.
   * @param {dotNavigationPropsType} [props] - Component props.
   */
  function DotNavigation(props) {
    var _context;

    var _this;

    _classCallCheck(this, DotNavigation);

    _this = _super.call(this, props);

    _defineProperty(_assertThisInitialized(_this), "stop", void 0);

    _this.stop = false;
    _this.state = {
      autoPosition: props.dotIndex
    }; // BIND METHODS

    _assertThisInitialized(_this).onSwitchHandler = _bindInstanceProperty(_context = _this.onSwitchHandler).call(_context, _assertThisInitialized(_this));
    return _this;
  }
  /**
   * @instance
   * @description Callback for switch event.
   * @param {number} index - Triggering dot index.
   * @returns {void} Void.
   */


  _createClass(DotNavigation, [{
    key: "onSwitchHandler",
    value: function onSwitchHandler(index) {
      var _this2 = this;

      if (!this.stop) {
        if (typeof this.props.onSwitch === 'function') {
          var _context2;

          if (this.props.waitForCallback) {
            this.stop = true;
          }

          this.props.onSwitch(index, _bindInstanceProperty(_context2 = function _context2() {
            _newArrowCheck(this, _this2);

            this.stop = false;
          }).call(_context2, this));
        }

        this.setState({
          autoPosition: index
        });
      }
    }
    /**
     * @instance
     * @description React render method.
     * @returns {Object<string,*>} React component.
     */

  }, {
    key: "render",
    value: function render() {
      var _this3 = this,
          _context3;

      var _this$props = this.props,
          auto = _this$props.auto,
          dotIndex = _this$props.dotIndex,
          quantity = _this$props.quantity,
          direction = _this$props.direction,
          size = _this$props.size,
          style = _this$props.style,
          className = _this$props.className,
          customStyles = _this$props.customStyles,
          customClassNames = _this$props.customClassNames,
          globalTheme = _this$props.globalTheme;
      var autoPosition = this.state.autoPosition;
      var globalStyles = globalTheme ? DotNavigation.globalStyles[globalTheme] || {} : {};
      var globalClassNames = globalTheme ? DotNavigation.globalClassNames[globalTheme] || {} : {};
      var mergedStyles = {
        root: mergeStyles(globalStyles.root, customStyles.root, style, {
          flexDirection: direction
        }),
        dotActive: mergeStyles(globalStyles.dot, globalStyles.dotActive, customStyles.dot, customStyles.dotActive, {
          height: size,
          width: size
        }),
        dotInactive: mergeStyles(globalStyles.dot, globalStyles.dotInactive, customStyles.dot, customStyles.dotInactive, {
          height: size,
          width: size
        })
      };
      var mergedClassNames = {
        root: mergeClassNames(resetClassNames.root, componentClassNames.root, globalClassNames.root, customClassNames.root, className),
        dotActive: mergeClassNames(componentClassNames.dotActive, globalClassNames.dot, globalClassNames.dotActive, customClassNames.dot, customClassNames.dotActive),
        dotInactive: mergeClassNames(componentClassNames.dotInactive, globalClassNames.dot, globalClassNames.dotInactive, customClassNames.dot, customClassNames.dotInactive)
      };
      return /*#__PURE__*/React.createElement("span", {
        style: mergedStyles.root,
        className: mergedClassNames.root
      }, _bindInstanceProperty(_context3 = function _context3() {
        _newArrowCheck(this, _this3);

        var i = 0;
        var output = [];
        var currentPosition = auto ? autoPosition : dotIndex;

        for (i = 0; i < quantity; i++) {
          var _context4;

          var isActiveDot = i === currentPosition;
          output.push( /*#__PURE__*/React.createElement("span", {
            key: i,
            onClick: _bindInstanceProperty(_context4 = this.onSwitchHandler).call(_context4, this, i),
            style: isActiveDot ? mergedStyles.dotActive : mergedStyles.dotInactive,
            className: isActiveDot ? mergedClassNames.dotActive : mergedClassNames.dotInactive
          }));
        }

        return output;
      }).call(_context3, this)());
    }
  }]);

  return DotNavigation;
}(React.Component);

_defineProperty(DotNavigation, "globalStyles", {
  default: {
    root: {},
    dot: {},
    dotActive: {},
    dotInactive: {}
  }
});

_defineProperty(DotNavigation, "globalClassNames", {
  default: {
    root: '',
    dot: '',
    dotActive: '',
    dotInactive: ''
  }
});

_defineProperty(DotNavigation, "defaultProps", {
  quantity: 0,
  dotIndex: 0,
  size: '17px',
  direction: 'row',
  auto: true,
  onSwitch: null,
  waitForCallback: false,
  style: {},
  className: '',
  customStyles: {},
  customClassNames: {},
  globalTheme: 'default'
});

export { DotNavigation };